import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('credentials');
  const [credentials, setCredentials] = useState(null);
  const [users, setUsers] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [testingFrappe, setTestingFrappe] = useState(false);
  const [testingMixpanel, setTestingMixpanel] = useState(false);
  const [frappeTestResult, setFrappeTestResult] = useState(null);
  const [mixpanelTestResult, setMixpanelTestResult] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'poc', password: '' });
  const [addUserError, setAddUserError] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  
  // User name editing state
  const [editingUserName, setEditingUserName] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Timesheet import state
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Defense-in-depth: Redirect non-super-admins
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === 'credentials') {
      fetchCredentials();
    } else if (activeTab === 'sync') {
      fetchSyncLogs();
      fetchSyncStatus();
    } else if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchCredentials = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/credentials`, {
        withCredentials: true
      });
      setCredentials(data);
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, {
        withCredentials: true
      });
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchSyncLogs = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sync/logs`, {
        withCredentials: true
      });
      setSyncLogs(data);
    } catch (error) {
      console.error('Failed to fetch sync logs:', error);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sync/status`, {
        withCredentials: true
      });
      setSyncStatus(data);
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  const handleTestFrappe = async () => {
    setTestingFrappe(true);
    setFrappeTestResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/settings/test-frappe`, {}, {
        withCredentials: true
      });
      setFrappeTestResult(data);
    } catch (error) {
      setFrappeTestResult({ success: false, error: error.message });
    } finally {
      setTestingFrappe(false);
    }
  };

  const handleTestMixpanel = async () => {
    setTestingMixpanel(true);
    setMixpanelTestResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/settings/test-mixpanel`, {}, {
        withCredentials: true
      });
      setMixpanelTestResult(data);
    } catch (error) {
      setMixpanelTestResult({ success: false, error: error.message });
    } finally {
      setTestingMixpanel(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      await axios.post(`${API_URL}/api/sync/trigger`, {}, {
        withCredentials: true
      });
      fetchSyncStatus();
      setTimeout(fetchSyncLogs, 2000);
    } catch (error) {
      console.error('Failed to trigger sync:', error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserError('');
    setAddingUser(true);

    try {
      // Use new invite endpoint (sends email)
      const response = await axios.post(
        `${API_URL}/api/users/invite`,
        {
          name: newUser.name,
          email: newUser.email,
          role: newUser.role
        },
        { withCredentials: true }
      );
      
      alert(`✅ Invitation sent to ${newUser.email}\n\nThey will receive an email with a Google login link.`);
      
      setNewUser({ name: '', email: '', role: 'poc', password: '' });
      setShowAddUser(false);
      fetchUsers();
    } catch (err) {
      setAddUserError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAddingUser(false);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await axios.patch(`${API_URL}/api/users/${userId}/status`, { status: newStatus }, {
        withCredentials: true
      });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId, userName, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}" (${userEmail})?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, {
        withCredentials: true
      });
      alert(`✅ User "${userName}" has been deleted successfully.`);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditUserName = (userId, currentName) => {
    setEditingUserName(userId);
    setEditNameValue(currentName);
  };

  const handleSaveUserName = async (userId) => {
    if (!editNameValue.trim()) {
      alert('Name cannot be empty');
      return;
    }

    setSavingName(true);
    try {
      await axios.patch(
        `${API_URL}/api/users/${userId}/name`,
        { name: editNameValue },
        { withCredentials: true }
      );
      setEditingUserName(null);
      setEditNameValue('');
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleCancelEditName = () => {
    setEditingUserName(null);
    setEditNameValue('');
  };


  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImportTimesheet = async () => {
    if (!importFile) {
      alert('Please select a file to upload');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const { data } = await axios.post(`${API_URL}/api/timesheet/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      setImportResult(data);
      setImportFile(null);
      // Reset file input
      const fileInput = document.getElementById('timesheet-file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to import timesheet';
      setImportResult({
        success: false,
        error: errorMsg
      });
    } finally {
      setImporting(false);
    }
  };


  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-900 border-purple-900';
      case 'admin':
        return 'bg-blue-100 text-blue-900 border-blue-900';
      case 'poc':
        return 'bg-[#D1FAE5] text-[#065F46] border-[#065F46]';
      default:
        return 'bg-[#F4F4F5] text-[#52525B] border-[#52525B]';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin':
        return 'SUPER ADMIN';
      case 'admin':
        return 'ADMIN';
      case 'poc':
        return 'POC';
      default:
        return role.toUpperCase();
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto">
        <h1 className="text-4xl tracking-tighter font-bold font-headings text-[#09090B] mb-8">
          Settings
        </h1>

        {/* Tabs */}
        <div className="bg-white border border-[#E4E4E7] mb-6">
          <div className="flex border-b border-[#E4E4E7]">
            <button
              onClick={() => setActiveTab('credentials')}
              data-testid="tab-credentials"
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'credentials'
                  ? 'bg-[#0C9168] text-white'
                  : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
              }`}
            >
              API Credentials
            </button>
            <button
              onClick={() => setActiveTab('sync')}
              data-testid="tab-sync"
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'sync'
                  ? 'bg-[#0C9168] text-white'
                  : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
              }`}
            >
              Sync
            </button>
            <button
              onClick={() => setActiveTab('users')}
              data-testid="tab-users"
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'users'
                  ? 'bg-[#0C9168] text-white'
                  : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
              }`}
            >
              User Management
            </button>
            <button
              onClick={() => setActiveTab('import')}
              data-testid="tab-import"
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'import'
                  ? 'bg-[#0C9168] text-white'
                  : 'bg-white text-[#52525B] hover:bg-[#F4F4F5]'
              }`}
            >
              Timesheet Import
            </button>
          </div>

          <div className="p-6">
            {/* Credentials Tab */}
            {activeTab === 'credentials' && credentials && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl tracking-tight font-semibold font-headings text-[#09090B] mb-4">
                    Frappe API
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                        Base URL
                      </label>
                      <input
                        type="text"
                        value={credentials.frappe_base_url}
                        disabled
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-[#F4F4F5] text-[#52525B] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                        API Key
                      </label>
                      <input
                        type="password"
                        value={credentials.frappe_api_key}
                        disabled
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-[#F4F4F5] text-[#52525B] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                        API Secret
                      </label>
                      <input
                        type="password"
                        value={credentials.frappe_api_secret}
                        disabled
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-[#F4F4F5] text-[#52525B] text-sm"
                      />
                    </div>
                    <button
                      onClick={handleTestFrappe}
                      disabled={testingFrappe}
                      data-testid="test-frappe-button"
                      className="bg-[#0C9168] text-white hover:bg-[#097352] px-6 py-2 font-medium transition-colors border border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {testingFrappe ? 'Testing...' : 'Test Frappe Connection'}
                    </button>
                    {frappeTestResult && (
                      <div className={`p-3 border text-sm ${
                        frappeTestResult.success
                          ? 'bg-green-50 border-green-600 text-green-900'
                          : 'bg-red-50 border-red-600 text-red-900'
                      }`}>
                        {frappeTestResult.success
                          ? `Success! Found ${frappeTestResult.firm_count} firms.`
                          : `Error: ${frappeTestResult.error}`}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h2 className="text-xl tracking-tight font-semibold font-headings text-[#09090B] mb-4">
                    Mixpanel API
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                        Project ID
                      </label>
                      <input
                        type="text"
                        value={credentials.mixpanel_project_id}
                        disabled
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-[#F4F4F5] text-[#52525B] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                        Service Account Username
                      </label>
                      <input
                        type="text"
                        value={credentials.mixpanel_service_username}
                        disabled
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-[#F4F4F5] text-[#52525B] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                        Service Account Password
                      </label>
                      <input
                        type="password"
                        value={credentials.mixpanel_service_password}
                        disabled
                        className="w-full px-4 py-2 border border-[#E4E4E7] bg-[#F4F4F5] text-[#52525B] text-sm"
                      />
                    </div>
                    <button
                      onClick={handleTestMixpanel}
                      disabled={testingMixpanel}
                      data-testid="test-mixpanel-button"
                      className="bg-[#0C9168] text-white hover:bg-[#097352] px-6 py-2 font-medium transition-colors border border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {testingMixpanel ? 'Testing...' : 'Test Mixpanel Connection'}
                    </button>
                    {mixpanelTestResult && (
                      <div className={`p-3 border text-sm ${
                        mixpanelTestResult.success
                          ? 'bg-green-50 border-green-600 text-green-900'
                          : 'bg-red-50 border-red-600 text-red-900'
                      }`}>
                        {mixpanelTestResult.success
                          ? mixpanelTestResult.message
                          : `Error: ${mixpanelTestResult.error}`}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-[#F4F4F5] border border-[#E4E4E7] p-4 text-sm text-[#52525B]">
                  <strong>Note:</strong> To update API credentials, please modify the <code className="font-mono bg-white px-2 py-1">.env</code> file and restart the server.
                </div>
              </div>
            )}

            {/* Sync Tab */}
            {activeTab === 'sync' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl tracking-tight font-semibold font-headings text-[#09090B] mb-4">
                    Sync Management
                  </h2>
                  {syncStatus && (
                    <div className="space-y-4 mb-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Status</div>
                          <div className="text-sm text-[#09090B]">
                            {syncStatus.is_running ? 'Running' : 'Idle'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-bold uppercase tracking-[0.1em] text-[#52525B] mb-1">Last Sync</div>
                          <div className="text-sm text-[#09090B]">
                            {syncStatus.last_sync
                              ? formatDistanceToNow(new Date(syncStatus.last_sync.started_at), { addSuffix: true })
                              : 'Never'}
                          </div>
                        </div>
                      </div>
                      {syncStatus.is_running && (
                        <div className="bg-[#D1FAE5] border border-[#0C9168] p-3 text-sm text-[#065F46]">
                          {syncStatus.message}
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleSyncNow}
                    disabled={syncStatus?.is_running}
                    data-testid="manual-sync-button"
                    className="bg-[#0C9168] text-white hover:bg-[#097352] px-6 py-2 font-medium transition-colors border border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {syncStatus?.is_running ? 'Sync Running...' : 'Run Sync Now'}
                  </button>
                </div>

                <div>
                  <h2 className="text-xl tracking-tight font-semibold font-headings text-[#09090B] mb-4">
                    Sync History
                  </h2>
                  {syncLogs.length > 0 ? (
                    <div className="bg-white border border-[#E4E4E7]">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                            <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Started At</th>
                            <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Duration</th>
                            <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Triggered By</th>
                            <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Date Range</th>
                            <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Added</th>
                            <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Deleted</th>
                            <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Firms Synced</th>
                            <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Errors</th>
                            <th className="text-left text-xs uppercase tracking-wider font-bold p-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncLogs.map((log) => {
                            // Use duration_seconds from backend if available (more accurate)
                            const duration = log.duration_seconds 
                              ? Math.round(log.duration_seconds)
                              : log.completed_at
                              ? Math.round((new Date(log.completed_at) - new Date(log.started_at)) / 1000)
                              : null;
                            
                            const hasErrors = log.status === 'failed' || (log.error_details && log.error_details.length > 0);
                            
                            return (
                              <>
                                <tr key={log._id} className="border-b border-[#E4E4E7]">
                                  <td className="p-3 text-sm text-[#09090B] font-mono">
                                    {new Date(log.started_at).toLocaleString()}
                                  </td>
                                  <td className="p-3 text-sm text-[#52525B] font-mono">
                                    {duration != null ? `${duration}s` : '—'}
                                  </td>
                                  <td className="p-3 text-sm text-[#09090B]">{log.triggered_by}</td>
                                  <td className="p-3 text-sm text-[#52525B] font-mono">{log.date_range || '—'}</td>
                                  <td className="p-3 text-sm text-[#0C9168] font-mono">{log.records_added || '—'}</td>
                                  <td className="p-3 text-sm text-[#DC2626] font-mono">{log.records_deleted || '—'}</td>
                                  <td className="p-3 text-sm text-[#09090B] font-mono">{log.firms_synced || 0}</td>
                                  <td className="p-3 text-sm text-[#DC2626] font-mono">{log.firms_failed || 0}</td>
                                  <td className="p-3">
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase border ${
                                        log.status === 'completed'
                                          ? 'bg-green-100 text-green-900 border-green-900'
                                          : log.status === 'running'
                                          ? 'bg-blue-100 text-blue-900 border-blue-900'
                                          : 'bg-red-100 text-red-900 border-red-900'
                                      }`}
                                    >
                                      {log.status}
                                    </span>
                                  </td>
                                </tr>
                                
                                {/* Error Details Row - Only show for failed syncs with error details */}
                                {hasErrors && (
                                  <tr className="bg-red-50 border-b border-[#E4E4E7]">
                                    <td colSpan="9" className="p-3">
                                      <div className="text-xs font-bold uppercase tracking-wider text-red-900 mb-2">
                                        Error Details:
                                      </div>
                                      <div className="space-y-1">
                                        {log.error_details && log.error_details.length > 0 ? (
                                          log.error_details.map((err, idx) => (
                                            <div key={idx} className="text-sm text-red-800 font-mono bg-red-100 p-2 border border-red-200">
                                              {err.site_url && <span className="font-bold">{err.site_url}: </span>}
                                              {err.error_message || JSON.stringify(err)}
                                            </div>
                                          ))
                                        ) : (
                                          <div className="text-sm text-red-800">No error details available</div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-[#52525B] py-4">No sync logs available</div>
                  )}
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-[#111827]">
                      User Management
                    </h2>
                    <p className="text-sm text-[#6B7280] mt-1">
                      Invite by Gmail. Roles: super_admin · admin · poc.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAddUser(!showAddUser)}
                    data-testid="add-user-button"
                    className="bg-[#111827] text-white hover:bg-[#1F2937] px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {showAddUser ? 'Cancel' : 'Invite user'}
                  </button>
                </div>

                {showAddUser && (
                  <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-6">
                    <form onSubmit={handleAddUser} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-[#374151] mb-1">
                            Name
                          </label>
                          <input
                            id="name"
                            type="text"
                            value={newUser.name}
                            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                            required
                            data-testid="new-user-name-input"
                            className="w-full px-3 py-2 border border-[#D1D5DB] bg-white text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#0C9168] focus:border-transparent text-sm"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-[#374151] mb-1">
                            Email
                          </label>
                          <input
                            id="email"
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            required
                            data-testid="new-user-email-input"
                            className="w-full px-3 py-2 border border-[#D1D5DB] bg-white text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#0C9168] focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                      <div className="w-1/2 pr-2">
                        <label htmlFor="role" className="block text-sm font-medium text-[#374151] mb-1">
                          Role
                        </label>
                        <select
                          id="role"
                          value={newUser.role}
                          onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                          required
                          data-testid="new-user-role-select"
                          className="w-full px-3 py-2 border border-[#D1D5DB] bg-white text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#0C9168] focus:border-transparent text-sm"
                        >
                          <option value="poc">POC</option>
                          <option value="admin">Admin</option>
                        </select>
                        <p className="mt-1 text-xs text-[#6B7280]">
                          Note: Cannot create Super Admin via invite
                        </p>
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
                        <p className="font-medium">📧 Email Invitation</p>
                        <p className="mt-1">An invitation email will be sent to <strong>{newUser.email || '(email address)'}</strong> with instructions to login using their Google account.</p>
                      </div>
                      
                      {addUserError && (
                        <div className="bg-red-50 border border-red-300 p-3 text-sm text-red-900">
                          {addUserError}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={addingUser}
                        data-testid="submit-new-user-button"
                        className="bg-[#111827] text-white hover:bg-[#1F2937] px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {addingUser ? 'Sending Invitation...' : 'Send Invitation'}
                      </button>
                    </form>
                  </div>
                )}

                {users.length > 0 ? (
                  <div className="bg-white">
                    <table className="w-full" data-testid="users-table">
                      <thead>
                        <tr className="border-b border-[#E5E7EB]">
                          <th className="text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider py-3 px-4">Email</th>
                          <th className="text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider py-3 px-4">Name</th>
                          <th className="text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider py-3 px-4">Role</th>
                          <th className="text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider py-3 px-4">Status</th>
                          <th className="text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider py-3 px-4">Last Login</th>
                          <th className="text-right text-xs font-medium text-[#6B7280] uppercase tracking-wider py-3 px-4"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E7EB]">
                        {users.map((u) => (
                          <tr key={u._id} className="hover:bg-[#F9FAFB] transition-colors">
                            <td className="py-4 px-4 text-sm text-[#111827]">{u.email}</td>
                            <td className="py-4 px-4 text-sm text-[#111827]">
                              {editingUserName === u.user_id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editNameValue}
                                    onChange={(e) => setEditNameValue(e.target.value)}
                                    className="px-2 py-1 border border-[#D1D5DB] bg-white text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#0C9168] text-sm"
                                  />
                                  <button
                                    onClick={() => handleSaveUserName(u.user_id)}
                                    disabled={savingName}
                                    className="text-[#0C9168] hover:text-[#097352] text-xs font-medium disabled:opacity-50"
                                  >
                                    {savingName ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={handleCancelEditName}
                                    disabled={savingName}
                                    className="text-[#52525B] hover:text-[#09090B] text-xs font-medium"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span>{u.name}</span>
                                  <button
                                    onClick={() => handleEditUserName(u.user_id, u.name)}
                                    className="text-[#0C9168] hover:text-[#097352]"
                                    title="Edit name"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  u.role === 'super_admin' ? 'bg-purple-500' : 
                                  u.role === 'admin' ? 'bg-blue-500' : 
                                  'bg-[#0C9168]'
                                }`}></span>
                                <span className="text-sm text-[#111827]">
                                  {u.role === 'super_admin' ? 'Super Admin' : 
                                   u.role === 'admin' ? 'Admin' : 
                                   'POC'}
                                </span>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                                u.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {u.status === 'active' ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-sm text-[#6B7280]">
                              {u.last_login ? formatDistanceToNow(new Date(u.last_login), { addSuffix: true }) : 'Never'}
                            </td>
                            <td className="py-4 px-4 text-right">
                              {u.role !== 'super_admin' && (
                                <button
                                  onClick={() => handleDeleteUser(u._id, u.name, u.email)}
                                  data-testid="delete-user-button"
                                  className="text-[#DC2626] hover:text-[#B91C1C] transition-colors"
                                  title="Delete user"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-[#6B7280] py-8">No users found</div>
                )}
              </div>
            )}


            {/* Timesheet Import Tab */}
            {activeTab === 'import' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl tracking-tight font-semibold font-headings text-[#09090B] mb-4">
                    Timesheet Import
                  </h2>
                  <p className="text-sm text-[#52525B] mb-6">
                    Upload Excel timesheets from your team. The system will automatically match the POC by name and import their entries.
                  </p>
                </div>

                <div className="bg-[#F4F4F5] border border-[#E4E4E7] p-6 space-y-4">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                      Excel File Requirements
                    </h3>
                    <ul className="text-sm text-[#52525B] space-y-1 list-disc list-inside">
                      <li>Row 3, Column B must contain the POC&apos;s name (e.g., &quot;J Maria Roopini&quot;)</li>
                      <li>Name must match exactly with a user in User Management</li>
                      <li>Columns: Start Time | End Time | Duration | Activity Category | RGA/NRA | Client/Lead Name | Notes</li>
                      <li>Date format: &quot;Wednesday, 01 Jul 2026&quot;</li>
                      <li><strong>New data will overwrite existing entries</strong> for the dates in the Excel file</li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.1em] text-[#09090B] mb-2">
                      Select Excel File
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        id="timesheet-file-input"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileSelect}
                        disabled={importing}
                        className="block w-full text-sm text-[#52525B] file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-medium file:bg-[#0C9168] file:text-white hover:file:bg-[#097352] file:cursor-pointer file:transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                    {importFile && (
                      <p className="text-sm text-[#0C9168] mt-2">
                        ✓ Selected: {importFile.name}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={handleImportTimesheet}
                    disabled={!importFile || importing}
                    data-testid="import-timesheet-button"
                    className="bg-[#0C9168] text-white hover:bg-[#097352] px-6 py-2 font-medium transition-colors border border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importing ? 'Importing...' : 'Import Timesheet'}
                  </button>
                </div>

                {/* Import Results */}
                {importResult && (
                  <div className={`p-4 border ${
                    importResult.success
                      ? 'bg-green-50 border-green-600'
                      : 'bg-red-50 border-red-600'
                  }`}>
                    {importResult.success ? (
                      <div>
                        <h3 className="text-lg font-bold text-green-900 mb-3">
                          ✓ Import Successful
                        </h3>
                        <div className="space-y-2 text-sm text-green-900">
                          <p><strong>POC:</strong> {importResult.poc_name} ({importResult.poc_email})</p>
                          <p><strong>Imported:</strong> {importResult.imported_count} entries</p>
                          <p><strong>Dates Processed:</strong> {importResult.dates_processed}</p>
                          {importResult.overwritten_count > 0 && (
                            <p className="text-blue-900"><strong>Overwritten:</strong> {importResult.overwritten_count} existing entries were replaced</p>
                          )}
                          {importResult.error_count > 0 && (
                            <div>
                              <p className="text-yellow-900"><strong>Errors:</strong> {importResult.error_count} rows failed</p>
                              {importResult.errors && importResult.errors.length > 0 && (
                                <ul className="text-xs mt-1 list-disc list-inside">
                                  {importResult.errors.map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-lg font-bold text-red-900 mb-2">
                          ✗ Import Failed
                        </h3>
                        <p className="text-sm text-red-900">
                          {importResult.error}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </Layout>
  );
}